'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Calendar,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  PartyPopper,
  Video,
  Link as LinkIcon,
} from 'lucide-react';
import type { EventSession, SessionExtra } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';

interface SessionPickerProps {
  sessions: EventSession[];
  registrationLabel: string;
}

type Step = 'campus' | 'detail';

const extraIconMap = {
  party: PartyPopper,
  video: Video,
  link: LinkIcon,
} as const;

export function SessionPicker({ sessions, registrationLabel }: SessionPickerProps) {
  const [step, setStep] = useState<Step>('campus');
  const [selected, setSelected] = useState<EventSession | null>(null);

  const selectCampus = (session: EventSession) => {
    setSelected(session);
    setStep('detail');
  };

  const slideVariants = {
    enter: { x: 40, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -40, opacity: 0 },
  };

  return (
    <div className="overflow-hidden">
      <AnimatePresence mode="wait">
        {/* Step 1: Pick campus */}
        {step === 'campus' && (
          <motion.div
            key="campus"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-2">
              {sessions.map((session) => (
                <button
                  key={session.ID}
                  type="button"
                  onClick={() => selectCampus(session)}
                  className="border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
                >
                  <div>
                    <p className="text-foreground flex items-center gap-1.5 text-sm font-bold">
                      <MapPin className="h-3.5 w-3.5 text-[var(--brand-secondary)]" />
                      {session.Label}
                    </p>
                    <p className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                      <Calendar className="h-3 w-3" />
                      {session.Dates}
                    </p>
                  </div>
                  <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Campus detail — RSVP + extras */}
        {step === 'detail' && selected && (
          <motion.div
            key="detail"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              onClick={() => setStep('campus')}
              className="text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1 text-xs font-medium transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {selected.Label}
            </button>

            {/* RSVP / Register */}
            {selected.Registration_URL && (
              <a
                href={selected.Registration_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 bg-[var(--brand-secondary)] px-5 py-3 text-sm font-bold tracking-wide text-white uppercase transition-opacity hover:opacity-90 active:scale-[0.98]"
              >
                {registrationLabel}
                <ExternalLink className="h-4 w-4" />
              </a>
            )}

            {/* Extras */}
            {selected.Extras && selected.Extras.length > 0 && (
              <div className="mt-4">
                <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase">
                  More at {selected.Label}
                </p>
                <div className="space-y-2">
                  {selected.Extras.map((extra) => (
                    <ExtraCard key={extra.Label} extra={extra} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ExtraCard({ extra }: { extra: SessionExtra }) {
  const Icon = extraIconMap[extra.Icon];

  return (
    <a
      href={extra.URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group border-border bg-background flex items-start gap-3 border p-3 transition-shadow hover:shadow-md"
    >
      {extra.Image_URL ? (
        <div className="h-16 w-24 shrink-0 overflow-hidden">
          <Img
            src={extra.Image_URL}
            alt={extra.Label}
            className="h-full w-full object-cover transition-transform duration-[650ms] ease-out group-hover:scale-[1.025]"
          />
        </div>
      ) : (
        <div className="bg-muted/50 flex h-16 w-16 shrink-0 items-center justify-center">
          <Icon className="h-6 w-6 text-[var(--brand-secondary)]" />
        </div>
      )}

      <div className="flex flex-1 items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-foreground text-sm font-bold">{extra.Label}</p>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            {extra.Description}
          </p>
        </div>
        <ExternalLink className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0 transition-colors group-hover:text-[var(--brand-secondary)]" />
      </div>
    </a>
  );
}
